import type { EmotionStyle } from '@hotjar/webapp/features/Feedback/domain/EmotionStyle';
import { EmotionIcon } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import { numberWithOrdinal, roundNumber } from '@hotjar/insights-common/lib/number';
import { readableMonth } from '@hotjar/webapp/lib/time';
import { tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { EmotionScore } from '@hotjar/insights-common/constants/emotions';
import { Trans, useTranslation } from '@hotjar/translations';

const S = {
  Container: styled.div`
    ${tokens.text('body2')};
    color: ${tokens.color.text_default_inverse};
    background-color: ${tokens.color.neutral_800};
    border-radius: ${tokens.radius.m};
    ${tokens.elevation(1)};
    padding: ${tokens.spacer.x1};
    min-width: 180px;
  `,
  Caption: styled.div`
    padding-top: ${tokens.spacer.x0};
    ${tokens.text('caption')};
    grid-gap: ${tokens.spacer.x0};
    display: flex;
    white-space: nowrap;
    align-items: center;
  `,
  Responses: styled.span`
    color: ${tokens.color.text_light};
    flex-shrink: 0;
  `,
  Score: styled.span`
    flex-shrink: 0;
  `,
};

export interface FeedbackSentimentGraphTooltipProps {
  sampleDate: Date;
  score: number;
  emotionStyle: EmotionStyle;
  totalResponses: number;
}

export function FeedbackSentimentGraphTooltip({
  sampleDate,
  score,
  totalResponses,
  emotionStyle,
}: FeedbackSentimentGraphTooltipProps): JSX.Element {
  const { t } = useTranslation('feedback');

  return (
    <S.Container>
      {numberWithOrdinal(sampleDate.getDate())} {readableMonth(sampleDate.getMonth())}
      <S.Caption>
        <EmotionIcon
          emotionStyle={emotionStyle}
          score={roundNumber(score) as EmotionScore}
          size={emotionStyle === 'star' ? 's' : 'm'}
        />
        <S.Score>
          <Trans i18nKey="feedback:incoming-feedback.summary.score-out-of-5">
            {{ value: roundNumber(score + 1, 1) }}
          </Trans>
        </S.Score>

        <S.Responses>({t('count-responses', { count: totalResponses })})</S.Responses>
      </S.Caption>
    </S.Container>
  );
}
